import { constantify } from '@/utils'

export const TRANSACTION_KINDS = {
  credit: 'CREDIT',
  debit: 'DEBIT',
} as const

export const TRANSACTION_PAYMENT_CHANNELS = {
  inStore: 'IN_STORE',
  online: 'ONLINE',
  other: 'OTHER',
  inPerson: 'IN_PERSON',
} as const

export const PAYMENT_CHANNELS_OPTIONS = Object.values(
  TRANSACTION_PAYMENT_CHANNELS,
)

export const STATEMENT_KINDS = {
  creditCard: 'CREDIT_CARD',
  bank: 'BANK',
} as const

export const STATEMENT_MAX_UPLOAD_SIZE_MB = 100

export const STATEMENT_STATUSES = {
  notStarted: 'NOT_STARTED',
  enqueued: 'ENQUEUED',
  succeeded: 'SUCCEEDED',
  failed: 'FAILED',
  segmenting: 'SEGMENTING',
  recognizing: 'RECOGNIZING',
  extracting: 'EXTRACTING',
  categorizing: 'CATEGORIZING',
  duplicated: 'DUPLICATED',
} as const

// Reconcile verdict per cycle, rolled up to the statement.
// UNKNOWN = processed before reconciliation existed (or not yet reprocessed), reads as "Not checked".
// PARTIALLY_RECONCILED is a statement rollup only: a cycle is never partly anything.
export const RECONCILIATION_STATUSES = {
  unknown: 'UNKNOWN',
  reconciled: 'RECONCILED',
  partiallyReconciled: 'PARTIALLY_RECONCILED',
  needsReview: 'NEEDS_REVIEW',
  unverifiable: 'UNVERIFIABLE',
  // The engine chose not to read the cycle at all (no register on the page).
  skipped: 'SKIPPED',
} as const

export const PROJECT_STATUSES = {
  notStarted: 'NOT_STARTED',
  succeeded: 'SUCCEEDED',
  failed: 'FAILED',
  processing: 'PROCESSING',
  combining: 'COMBINING',
  combineFailed: 'COMBINE_FAILED',
  duplicating: 'DUPLICATING',
  duplicateFailed: 'DUPLICATE_FAILED',
} as const

export const INSIGHTS_STATUSES = {
  notStarted: 'NOT_STARTED',
  enqueued: 'ENQUEUED',
  succeeded: 'SUCCEEDED',
  failed: 'FAILED',
  processing: 'PROCESSING',
}

export const PROJECT_REPORT_STATUSES = {
  notStarted: 'NOT_STARTED',
  generated: 'GENERATED',
  failed: 'FAILED',
  generating: 'GENERATING',
} as const

export const ACCOUNT_TYPES = [
  'Credit card',
  'Checking',
  'Savings',
  'Debit card',
  'Loan',
  'Investment',
  'Retirement',
  'Other',
] as const

export const CATEGORIES_DICT = {
  INCOME: [
    'DIVIDENDS',
    'INTEREST_EARNED',
    'RETIREMENT_PENSION',
    'TAX_REFUND',
    'UNEMPLOYMENT',
    'WAGES',
    'OTHER_INCOME',
  ],
  TRANSFER_IN: [
    'CASH_ADVANCES_AND_LOANS',
    'DEPOSIT',
    'INVESTMENT_AND_RETIREMENT_FUNDS',
    'SAVINGS',
    'ACCOUNT_TRANSFER',
    'OTHER_TRANSFER_IN',
  ],
  TRANSFER_OUT: [
    'INVESTMENT_AND_RETIREMENT_FUNDS',
    'SAVINGS',
    'WITHDRAWAL',
    'ACCOUNT_TRANSFER',
    'OTHER_TRANSFER_OUT',
  ],
  LOAN_PAYMENTS: [
    'CAR_PAYMENT',
    'CREDIT_CARD_PAYMENT',
    'PERSONAL_LOAN_PAYMENT',
    'MORTGAGE_PAYMENT',
    'STUDENT_LOAN_PAYMENT',
    'OTHER_PAYMENT',
  ],
  BANK_FEES: [
    'ATM_FEES',
    'FOREIGN_TRANSACTION_FEES',
    'INSUFFICIENT_FUNDS',
    'INTEREST_CHARGE',
    'OVERDRAFT_FEES',
    'OTHER_BANK_FEES',
  ],
  ENTERTAINMENT: [
    'CASINOS_AND_GAMBLING',
    'MUSIC_AND_AUDIO',
    'SPORTING_EVENTS_AMUSEMENT_PARKS_AND_MUSEUMS',
    'TV_AND_MOVIES',
    'VIDEO_GAMES',
    'OTHER_ENTERTAINMENT',
  ],
  FOOD_AND_DRINK: [
    'BEER_WINE_AND_LIQUOR',
    'COFFEE',
    'FAST_FOOD',
    'GROCERIES',
    'RESTAURANT',
    'VENDING_MACHINES',
    'OTHER_FOOD_AND_DRINK',
  ],
  GENERAL_MERCHANDISE: [
    'BOOKSTORES_AND_NEWSSTANDS',
    'CLOTHING_AND_ACCESSORIES',
    'CONVENIENCE_STORES',
    'DEPARTMENT_STORES',
    'DISCOUNT_STORES',
    'ELECTRONICS',
    'GIFTS_AND_NOVELTIES',
    'OFFICE_SUPPLIES',
    'ONLINE_MARKETPLACES',
    'PET_SUPPLIES',
    'SPORTING_GOODS',
    'SUPERSTORES',
    'TOBACCO_AND_VAPE',
    'OTHER_GENERAL_MERCHANDISE',
  ],
  HOME_IMPROVEMENT: [
    'FURNITURE',
    'HARDWARE',
    'REPAIR_AND_MAINTENANCE',
    'SECURITY',
    'OTHER_HOME_IMPROVEMENT',
  ],
  MEDICAL: [
    'DENTAL_CARE',
    'EYE_CARE',
    'NURSING_CARE',
    'PHARMACIES_AND_SUPPLEMENTS',
    'PRIMARY_CARE',
    'VETERINARY_SERVICES',
    'OTHER_MEDICAL',
  ],
  PERSONAL_CARE: [
    'GYMS_AND_FITNESS_CENTERS',
    'HAIR_AND_BEAUTY',
    'LAUNDRY_AND_DRY_CLEANING',
    'OTHER_PERSONAL_CARE',
  ],
  GENERAL_SERVICES: [
    'ACCOUNTING_AND_FINANCIAL_PLANNING',
    'AUTOMOTIVE',
    'CHILDCARE',
    'CONSULTING_AND_LEGAL',
    'EDUCATION',
    'INSURANCE',
    'POSTAGE_AND_SHIPPING',
    'STORAGE',
    'OTHER_GENERAL_SERVICES',
  ],
  GOVERNMENT_AND_NON_PROFIT: [
    'DONATIONS',
    'GOVERNMENT_DEPARTMENTS_AND_AGENCIES',
    'TAX_PAYMENT',
    'OTHER_GOVERNMENT_AND_NON_PROFIT',
  ],
  TRANSPORTATION: [
    'BIKES_AND_SCOOTERS',
    'GAS',
    'PARKING',
    'PUBLIC_TRANSIT',
    'TAXIS_AND_RIDE_SHARES',
    'TOLLS',
    'OTHER_TRANSPORTATION',
  ],
  TRAVEL: ['FLIGHTS', 'LODGING', 'RENTAL_CARS', 'OTHER_TRAVEL'],
  RENT_AND_UTILITIES: [
    'GAS_AND_ELECTRICITY',
    'INTERNET_AND_CABLE',
    'RENT',
    'SEWAGE_AND_WASTE_MANAGEMENT',
    'TELEPHONE',
    'WATER',
    'OTHER_UTILITIES',
  ],
} as const

export const CATEGORY_OPTIONS = Object.keys(CATEGORIES_DICT)

export const getCategoryDetailOptions = (category?: string) => {
  if (!category) return []

  return (
    CATEGORIES_DICT[constantify(category) as keyof typeof CATEGORIES_DICT] ?? []
  )
}

export const FAVORITE_TAG = {
  name: 'Favorites',
  color: '#FACC15',
} as const

// URL hash that scrolls the Transactions page to its ledger table. Coverage-grid "View transactions"
// lands there so the table, which sits below the accounts and charts, is in view on arrival.
export const TRANSACTIONS_TABLE_ANCHOR = 'transactions-table'

// Query param naming the billing cycle the reconciliation page opens its workspace on. Coverage-grid
// "Review reconciliation" writes it; `useCycleUrlSync` reads it.
export const CYCLE_PARAM = 'cycle'

export const PROJECT_TASK_STATUSES = {
  open: 'OPEN',
  done: 'DONE',
} as const
